import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import MapBackground from './MapBackground';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: var(--app-height, 100vh);
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Card = styled.div`
  background: rgba(20, 20, 20, 0.92);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px 32px;
  width: 95%;
  max-width: 480px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 10;
  animation: ${fadeIn} 0.5s ease;

  @media (max-width: 480px) {
    padding: 32px 22px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.6s ease;
`;

const PageIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 6px;
  letter-spacing: -0.3px;

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  margin: 0;
`;

const SectionLabel = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 16px;
  animation: ${fadeInUp} 0.6s ease 0.15s both;
`;

const ResourceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 28px;
`;

const ResourceCard = styled.button<{ accentFrom: string; accentTo: string; delay: number; disabled?: boolean }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  padding: 20px 18px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  text-align: left;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.5s ease ${props => 0.2 + props.delay * 0.1}s both;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${props => props.accentFrom}, ${props => props.accentTo});
    opacity: 0.8;
    transition: opacity 0.3s ease, width 0.3s ease;
  }

  &:hover {
    background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.06)'};
    border-color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.07)' : 'rgba(255, 255, 255, 0.12)'};
    transform: ${props => props.disabled ? 'none' : 'translateX(4px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 8px 30px rgba(0, 0, 0, 0.3)'};

    &::before {
      opacity: ${props => props.disabled ? 0.8 : 1};
      width: ${props => props.disabled ? '4px' : '5px'};
    }

    .resource-arrow {
      color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.5)'};
      transform: ${props => props.disabled ? 'none' : 'translateX(3px)'};
    }
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateX(2px)'};
  }
`;

const ResourceIcon = styled.div<{ bgFrom: string; bgTo: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.bgFrom}20, ${props => props.bgTo}30);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const ResourceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResourceName = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 3px;
`;

const ResourceDescription = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  line-height: 1.4;
`;

const ResourceArrow = styled.div`
  color: rgba(255, 255, 255, 0.2);
  font-size: 20px;
  transition: color 0.3s ease, transform 0.3s ease;
`;

const ComingSoonBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 3px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.35);
  margin-left: 8px;
  vertical-align: middle;
  background-image: linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent);
  background-size: 200% 100%;
  animation: ${shimmer} 3s ease infinite;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
  margin: 0 0 20px;
`;

const BackButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  -webkit-tap-highlight-color: transparent;
  animation: ${fadeInUp} 0.5s ease 0.5s both;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface ResourcesPageProps {
  onBack: () => void;
  onSignalBoxes: () => void;
  onHiPathAllocations: () => void;
}

const ResourcesPage: React.FC<ResourcesPageProps> = ({
  onBack,
  onSignalBoxes,
  onHiPathAllocations,
}) => {
  useEffect(() => {
    const handleResize = () => {
      const appHeight = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          <Header>
            <PageIcon>📚</PageIcon>
            <Title>Resources</Title>
            <Subtitle>Reference materials and documentation</Subtitle>
          </Header>

          <SectionLabel>Available Resources</SectionLabel>

          <ResourceGrid>
            <ResourceCard
              accentFrom="#d32f2f"
              accentTo="#b71c1c"
              delay={0}
              onClick={onSignalBoxes}
            >
              <ResourceIcon bgFrom="#d32f2f" bgTo="#b71c1c">
                📞
              </ResourceIcon>
              <ResourceInfo>
                <ResourceName>Signal Box Directory</ResourceName>
                <ResourceDescription>
                  Search signal box phone numbers - internal and external
                </ResourceDescription>
              </ResourceInfo>
              <ResourceArrow className="resource-arrow">›</ResourceArrow>
            </ResourceCard>

            <ResourceCard
              accentFrom="#1e88e5"
              accentTo="#1565c0"
              delay={1}
              onClick={onHiPathAllocations}
            >
              <ResourceIcon bgFrom="#1e88e5" bgTo="#1565c0">
                🚦
              </ResourceIcon>
              <ResourceInfo>
                <ResourceName>
                  Doncaster HiPath Allocations
                </ResourceName>
                <ResourceDescription>
                  A documented view of the current Doncaster HiPath allocations
                </ResourceDescription>
              </ResourceInfo>
              <ResourceArrow className="resource-arrow">›</ResourceArrow>
            </ResourceCard>

            <ResourceCard
              accentFrom="#7c3aed"
              accentTo="#6d28d9"
              delay={2}
              onClick={() => {}}
              disabled
            >
              <ResourceIcon bgFrom="#7c3aed" bgTo="#6d28d9">
                🔌
              </ResourceIcon>
              <ResourceInfo>
                <ResourceName>
                  SPT FTN Allocations
                  <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                </ResourceName>
                <ResourceDescription>
                  Detailed FTN site SPT allocation records across the network
                </ResourceDescription>
              </ResourceInfo>
              <ResourceArrow className="resource-arrow">›</ResourceArrow>
            </ResourceCard>
          </ResourceGrid>

          <Divider />

          <BackButton onClick={onBack}>
            Back to Main Menu
          </BackButton>
        </Card>
      </Container>
    </>
  );
};

export default ResourcesPage;
